import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Globe, ChevronDown, Check, Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';

const countries = [
  'United States', 'United Kingdom', 'Canada', 'Australia', 'Germany',
  'France', 'Spain', 'Italy', 'Netherlands', 'Belgium', 'Switzerland',
  'Austria', 'Sweden', 'Norway', 'Denmark', 'Finland', 'Ireland',
  'Portugal', 'Poland', 'Czech Republic', 'Hungary', 'Romania',
  'Japan', 'South Korea', 'China', 'Hong Kong', 'Taiwan', 'Singapore',
  'Malaysia', 'Thailand', 'Vietnam', 'Philippines', 'Indonesia',
  'India', 'Pakistan', 'Bangladesh', 'Sri Lanka', 'Nepal',
  'Brazil', 'Argentina', 'Mexico', 'Colombia', 'Chile', 'Peru',
  'South Africa', 'Nigeria', 'Kenya', 'Ghana', 'Egypt', 'Morocco',
  'UAE', 'Saudi Arabia', 'Qatar', 'Kuwait', 'Bahrain', 'Oman',
  'Israel', 'Turkey', 'Russia', 'Ukraine',
  'New Zealand', 'Other'
];

export default function CountrySelector({ value, onChange, error, className }) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);

  // Filter countries based on search
  const filteredCountries = countries.filter(country =>
    country.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Handle clicking a country - use useCallback for stable reference
  const handleCountryClick = useCallback((country, e) => {
    e.preventDefault();
    e.stopPropagation();
    onChange(country);
    setIsOpen(false);
    setSearchTerm('');
  }, [onChange]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    if (isOpen) {
      // Use capture phase for better event handling
      document.addEventListener('mousedown', handleClickOutside, true);
      document.addEventListener('touchstart', handleClickOutside, true);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
      document.removeEventListener('touchstart', handleClickOutside, true);
    };
  }, [isOpen]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      const timer = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  // Prevent body scroll when open on mobile
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const toggleDropdown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(prev => !prev);
    if (isOpen) {
      setSearchTerm('');
    }
  };

  const closeDropdown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(false);
    setSearchTerm('');
  };

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={toggleDropdown}
        onTouchEnd={(e) => {
          e.preventDefault();
          toggleDropdown(e);
        }}
        className={cn(
          "input-field flex items-center justify-between w-full cursor-pointer select-none",
          !value && "text-white/40",
          error && "border-hotpink-400/50 focus:border-hotpink-400",
          isOpen && "border-cyan-500/50 ring-1 ring-cyan-500/50"
        )}
        data-testid="register-country-select"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <div className="flex items-center gap-2 flex-1 min-w-0 text-left pointer-events-none">
          <Globe className="w-5 h-5 text-white/30 shrink-0" />
          <span className={cn("truncate", value ? "text-white" : "text-white/40")}>
            {value || "Select country"}
          </span>
        </div>
        <ChevronDown className={cn(
          "w-4 h-4 text-white/50 shrink-0 transition-transform duration-200 pointer-events-none",
          isOpen && "rotate-180"
        )} />
      </button>

      {/* Dropdown Portal */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-[99999]"
          style={{ touchAction: 'none' }}
        >
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/60"
            onClick={closeDropdown}
            onTouchEnd={closeDropdown}
          />
          
          {/* Dropdown Content - Always positioned at bottom on all screens for consistency */}
          <div
            className="absolute bottom-0 left-0 right-0 bg-[#0a0f1a] border-t border-white/20 rounded-t-2xl shadow-2xl max-h-[70vh] flex flex-col animate-slide-up"
            onClick={(e) => e.stopPropagation()}
            onTouchEnd={(e) => e.stopPropagation()}
            role="listbox"
            style={{ touchAction: 'pan-y' }}
          >
            {/* Handle bar */}
            <div className="flex justify-center py-2">
              <div className="w-10 h-1 bg-white/20 rounded-full" />
            </div>

            {/* Header */}
            <div className="px-4 pb-2 flex items-center justify-between">
              <h3 className="text-white font-semibold text-lg">Select Country</h3>
              <button
                type="button"
                onClick={closeDropdown}
                onTouchEnd={(e) => {
                  e.preventDefault();
                  closeDropdown(e);
                }}
                className="p-2 hover:bg-white/10 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-white/60" />
              </button>
            </div>

            {/* Search Input */}
            <div className="px-4 pb-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40 pointer-events-none" />
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search countries..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 text-white placeholder:text-white/40 rounded-lg h-11 pl-10 pr-10 text-base focus:outline-none focus:border-cyan-500/50"
                  data-testid="country-search-input"
                />
                {searchTerm && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSearchTerm('');
                    }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 hover:bg-white/10 rounded-full"
                  >
                    <X className="w-4 h-4 text-white/40" />
                  </button>
                )}
              </div>
            </div>

            {/* Country List */}
            <div 
              className="flex-1 overflow-y-auto overscroll-contain px-2"
              style={{ WebkitOverflowScrolling: 'touch' }}
            >
              {filteredCountries.length === 0 ? (
                <div className="p-6 text-center text-white/50">
                  No countries found
                </div>
              ) : (
                <div className="pb-4">
                  {filteredCountries.map((country) => (
                    <button
                      key={country}
                      type="button"
                      onClick={(e) => handleCountryClick(country, e)}
                      onTouchEnd={(e) => {
                        e.preventDefault();
                        handleCountryClick(country, e);
                      }}
                      className={cn(
                        "w-full flex items-center justify-between px-4 py-3.5 text-left rounded-lg transition-colors mb-1",
                        "hover:bg-cyan-500/20 active:bg-cyan-500/30",
                        "focus:bg-cyan-500/20 focus:outline-none",
                        "touch-manipulation",
                        value === country && "bg-cyan-500/20"
                      )}
                      role="option"
                      aria-selected={value === country}
                      data-testid={`country-option-${country.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <span className={cn(
                        "text-base",
                        value === country ? "text-cyan-400 font-medium" : "text-white/90"
                      )}>
                        {country}
                      </span>
                      {value === country && (
                        <Check className="w-5 h-5 text-cyan-400 shrink-0" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Safe area padding for iOS */}
            <div className="h-6 bg-[#0a0f1a]" />
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .animate-slide-up {
          animation: slide-up 0.25s ease-out forwards;
        }
      `}</style>
    </div>
  );
}
